// TalkPlusClientContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react'
import { Client } from 'talkplus-sdk' // TalkPlus 라이브러리 import
import env from 'config/env'
import { getTalkPlusLoginToken } from 'api/socket.request'
import { useRecoilValue } from 'recoil'
import { userState } from 'store/user'
import { useLocation } from 'react-router-dom'
import { protectedServerError } from 'api/serverErrorStatus'
// TalkPlus.Client 인스턴스를 생성합니다.
const client = new Client({ appId: env.TALK_PLUS_APP_ID })

// Context 타입 정의
interface TalkPlusClientContextType {
    client: Client
    isTalkplusLoginComplete: boolean
    isPageFocused: boolean // 페이지 포커스 여부를 추가
    talkplusLoginErrorCode: string
    isTalkplusLoginError: boolean
    talkplLoginErrorStatus: string
}

// Context를 생성합니다. 초기값은 클라이언트 인스턴스입니다.
const TalkPlusClientContext = createContext<TalkPlusClientContextType | undefined>(undefined)

const TalkPlusClientProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const location = useLocation()
    const [isTalkplusLoginComplete, setIsTalkplusLoginComplete] = useState<boolean>(false)
    // 톡플러스 로그인 오류 상태 관리
    const [isTalkplusLoginError, setIsTalkplusLoginError] = useState<boolean>(false)
    // 톡플러스 로그인 오류 코드 상태 관리
    const [talkplusLoginErrorCode, setTalkplusLoginErrorCode] = useState<string>('')
    // 톡플러스 로그인 오류 상태 관리
    const [talkplLoginErrorStatus, setTalkplLoginErrorStatus] = useState<string>('')
    const [isPageFocused, setIsPageFocused] = useState<boolean>(true)
    const user = useRecoilValue(userState)

    // 페이지 포커스 상태를 감지하는 useEffect
    useEffect(() => {
        const handleFocus = () => {
            setIsPageFocused(true)
        }

        const handleBlur = () => {
            setIsPageFocused(false)
        }

        window.addEventListener('focus', handleFocus)
        window.addEventListener('blur', handleBlur)

        return () => {
            window.removeEventListener('focus', handleFocus)
            window.removeEventListener('blur', handleBlur)
        }
    }, [])

    // 톡플러스 로그인 토큰 가져오기
    const fetchTalkPlusLoginToken = async () => {
        try {
            const response = await getTalkPlusLoginToken()
            return response.data.data
        } catch (err) {
            throw err
        }
    }

    // 톡플러스 로그인 처리
    useEffect(() => {
        const loginToTalkPlus = async () => {
            // 로그인 필요 없는 경우 체크
            const isLivePage = location.pathname.includes('/live')
            const isAlreadyLoggedIn = client.isLoggedIn()

            if (!user || isLivePage || isAlreadyLoggedIn) {
                // 톡플러스 로그인을 할 필요가 없는 경우 로그인 하지 않음
                return
            }

            try {
                const cmTalkplusData = await fetchTalkPlusLoginToken()

                if (!cmTalkplusData || !cmTalkplusData.loginToken || !cmTalkplusData.user) {
                    // 클메에 톡플러스 데이터가 없다면 톡플러스 로그인 실행하지 않음
                    return
                }

                // 로그인 처리
                await client.loginWithToken({
                    userId: cmTalkplusData.user.id,
                    loginToken: cmTalkplusData.loginToken,
                })

                // 로그인 완료 플래그 설정
                setIsTalkplusLoginComplete(true)
            } catch (err: any) {
                // 톡플러스 로그인 오류 상태 설정
                setIsTalkplusLoginError(true)

                if ('code' in err) {
                    const isServerError = protectedServerError(err.code)
                    if (isServerError) {
                        // 5xx 서버 에러 발생 시 에러 코드 저장
                        setTalkplLoginErrorStatus(err.code)
                        return
                    }
                    if (err.code) {
                        // 에러 발생 시 에러 코드 저장
                        setTalkplusLoginErrorCode(err.code)
                        return
                    }
                }
            }
        }

        loginToTalkPlus()
    }, [user, location.pathname])

    return (
        <TalkPlusClientContext.Provider
            value={{
                client,
                isTalkplusLoginComplete,
                isPageFocused,
                talkplusLoginErrorCode,
                isTalkplusLoginError,
                talkplLoginErrorStatus,
            }}
        >
            {children}
        </TalkPlusClientContext.Provider>
    )
}

// Context를 사용하는 커스텀 훅을 만듭니다.
export const useTalkPlusClient = (): TalkPlusClientContextType => {
    const context = useContext(TalkPlusClientContext)
    if (!context) {
        throw new Error('provider가 존재해야 함')
    }
    return context
}

export default TalkPlusClientProvider
